import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { useCasePayments } from '@/features/finance/api/useFinance'
import { PaymentAllocations } from '@/features/finance/components/PaymentAllocations'
import { PaymentStatusTag } from '@/features/finance/components/PaymentStatusTag'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/format'
import type { Payment } from '@/types/domain'

/* The money that has actually MOVED on one case, and what each payment settles.
 *
 * The case money block states positions — what is owed, what is forecast — and
 * every one of them is arithmetic over rows nobody can see from that screen.
 * This is those rows: each payment, its booking, and the allocations that turn
 * "cash arrived" into "this invoice is settled".
 *
 * Unallocated cash is shown as a FIGURE rather than as a warning (AD-11). Money
 * received and not yet applied is legal and ordinary — the platform is forbidden
 * to guess what it settles, so somebody has to say, and until they do the
 * balance simply stands there being true.
 *
 * Every write inside this block waits for the server (UX-DR-24); see
 * PaymentAllocations, which holds the act and explains why its list is the
 * server's rows rather than the mutation's own variables.
 *
 * ── Why this is a table, in the same grid as the cost lines above it ────────
 *
 * It was a list, and the list was the problem twice over.
 *
 * ACROSS: five facts about a payment — what it is, what the bank took, what is
 * still unapplied, how much, when — were spread over three lines with the
 * reference at one edge of a 1500px card and its figure at the other, ~950px
 * apart. The two facts that could have filled that gap were the ones stacked
 * BELOW it. Vertical space spent to leave horizontal space empty.
 *
 * DOWN: two payments were 16px apart with nothing between them, while INSIDE
 * one payment sat a fee line, a balance line, a second eyebrow, two or three
 * buttons and a list. More visual events within an item than between items —
 * the eye read the block as one ledger and had to re-derive where a payment
 * began.
 *
 * Both are what a table answers. Five named columns, one line per payment, the
 * figures in the same right-set columns the cost table uses; and the
 * allocations underneath as a full-width second row on the same payment — the
 * `UnitTableRows` pattern, where a row's detail opens under it rather than
 * beside it.
 *
 * The fee stays in its OWN column and is never added to the amount, which is
 * the whole of the rule it was split out for: the amount is what SETTLED, the
 * fee is what the transfer COST, and no surface here may put the two in one
 * total (AD-11). A column named «Комиссия» says that more plainly than a
 * sentence underneath ever did.
 */
export function CasePayments({
  caseId,
  direction,
  className,
}: {
  caseId: string
  /** One side of the ledger only (S-11 revision): «Счета клиенту» lists the
   *  case's `in` payments under «Полученные платежи», and the outbound ones
   *  are «Затраты»' «Платежи поставщикам», grouped by supplier. A direction is
   *  the payment's own (`payment.go`): `in` includes a recovery from a
   *  counterparty, and is listed here like any other `in`. Absent lists both. */
  direction?: Payment['direction']
  /** The caller's rule above this block. It defaults to the hairline that
   *  separates two bands of the same kind and is overridden to the heavier
   *  `border-line` when this block OPENS the record group — which happens on a
   *  case with no tranche series, where the expected-receipts table that
   *  normally carries that rule renders nothing at all. */
  className?: string
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const { data: read, isPending, isError } = useCasePayments(caseId)

  /* Silent while loading and silent on failure, unlike most sections. This is a
     block INSIDE the money card, and a spinner or an error panel nested in a
     card that already rendered reads as the whole card having failed. The money
     figures above it are computed server-side from these same rows, so they are
     already saying something true; this block re-appears when its query does. */
  if (isPending || isError) return null

  /* Filtered here, from the one read every payment write invalidates: a
     second, direction-scoped request would be a second answer about the same
     rows. */
  const payments = direction ? read.filter((payment) => payment.direction === direction) : read

  return (
    <section className={cn('border-line-hair border-t pt-4', className)}>
      <h3 className="asa-eyebrow px-[18px]">
        {direction === 'in'
          ? t('cases.money.payments.receivedHeading')
          : t('cases.money.payments.heading')}
      </h3>

      {payments.length === 0 ? (
        <EmptyState
          title={
            direction === 'in'
              ? t('cases.money.payments.receivedEmptyTitle')
              : t('cases.money.payments.emptyTitle')
          }
          description={
            direction === 'in'
              ? t('cases.money.payments.receivedEmptyDescription')
              : t('cases.money.payments.emptyDescription')
          }
          className="py-6"
        />
      ) : (
        <div className="asa-scroll mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-line border-b">
                <th scope="col" className="asa-eyebrow h-9 px-[18px]">
                  {t('cases.money.payments.columns.payment')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3 text-right">
                  {t('cases.money.payments.columns.fee')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3 text-right">
                  {t('cases.money.payments.columns.unallocated')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3 text-right">
                  {t('cases.money.payments.columns.amount')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-[18px] text-right">
                  {t('cases.money.payments.columns.date')}
                </th>
              </tr>
            </thead>

            {/* One `<tbody>` per payment: the reading row and its allocations
                are one record, and the group is what the rule between them
                separates. `border-b` on the body rather than on the last row,
                so the boundary lands under the detail and not inside it. */}
            {payments.map((payment) => (
              <tbody key={payment.id} className="border-line-hair border-b last:border-b-0">
                <tr className="text-[13px]">
                  {/* `font-medium`, and it is the one weight here that is not
                      decoration: an allocation row underneath carries the
                      vendor's own name in the heading colour, and at equal
                      weight the CHILD read as the more important of the two. */}
                  <td className="text-text-heading px-[18px] py-2.5 align-middle font-medium">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate">
                        {t(`finance.payments.${payment.direction}`)} · {payment.reference}
                      </span>
                      {/* The badge rides beside the reference, where the eye
                          already is. It says nothing about the MONEY — a
                          pending payment is inside every figure on the card
                          above (spec L4) — only that nobody has checked the
                          record yet. */}
                      <PaymentStatusTag status={payment.status} />
                    </span>
                  </td>

                  {/* What the bank took to move it, in its own column and shown
                      only when a fee was recorded: an absent fee is not a fee
                      of zero, and «0.00» here would claim the transfer was
                      free. */}
                  <td className="asa-num text-text-muted px-3 py-2.5 text-right align-middle text-[12.5px]">
                    {payment.fee
                      ? formatMoney(payment.fee.amount, payment.fee.currency)
                      : '—'}
                  </td>

                  {/* The unallocated balance, stated rather than implied by the
                      absence of allocation rows below — a payment with no rows
                      and a payment fully applied look alike at a glance
                      otherwise. Zero is the ordinary answer and is printed as
                      one, not dashed away. */}
                  <td
                    className={cn(
                      'asa-num px-3 py-2.5 text-right align-middle text-[12.5px]',
                      Number(payment.unallocated.amount) > 0
                        ? 'text-attention'
                        : 'text-text-muted',
                    )}
                  >
                    {formatMoney(
                      payment.unallocated.amount,
                      payment.unallocated.currency,
                    )}
                  </td>

                  <td className="asa-num text-text-heading px-3 py-2.5 text-right align-middle font-semibold">
                    {formatMoney(payment.amount.amount, payment.amount.currency)}
                  </td>

                  {/* `text-muted`, not `text-faint`: when the payment arrived is
                      stated nowhere else on this block, and the faint ramp is
                      2.77:1 and reserved by tokens.css for text that is
                      decorative or repeated somewhere legible. */}
                  <td className="text-text-muted px-[18px] py-2.5 text-right align-middle text-[12px] font-light whitespace-nowrap">
                    {format.dateShort(payment.receivedOn)}
                  </td>
                </tr>

                {/* What this payment settles, under it and across the whole
                    table — the same shape a truck's carriage facts open in on
                    the leg page. Indented by the first column's own gutter so
                    it reads as belonging to the row above. */}
                <tr>
                  <td colSpan={5} className="px-[18px] pb-3 sm:pl-9">
                    <PaymentAllocations payment={payment} />
                  </td>
                </tr>
              </tbody>
            ))}
          </table>
        </div>
      )}
    </section>
  )
}
